import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { UMWELT_ANLAGEN } from "./enums";
import { ziffernFilter } from "./GrossZahlEingabe";

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  cardForced: {
    backgroundColor: "#FFF8E8",
    ...shorthands.borderColor(BRAND.gold),
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    cursor: "pointer",
  },
  checkbox: {
    accentColor: BRAND.purple,
    cursor: "pointer",
    width: "18px",
    height: "18px",
  },
  labelText: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
    fontStyle: "italic",
    paddingLeft: "28px",
    paddingTop: "4px",
  },
  detail: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    paddingBottom: "10px",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  fullSpan: {
    "@media (min-width: 640px)": {
      gridColumn: "1 / -1",
    },
  },
  radioRow: {
    display: "flex",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
});

export type UmweltSelections = Record<string, Record<string, string>>;

type Props = {
  value: UmweltSelections;
  onChange: (next: UmweltSelections) => void;
  /** When true, "Öltanks" is locked-on with an explanatory notice. */
  oilTanksForced: boolean;
};

const UmweltChecklist = ({ value, onChange, oilTanksForced }: Props) => {
  const styles = useStyles();
  const isChecked = (name: string) =>
    value[name]?.selected === "true" || (name === "Öltanks" && oilTanksForced);
  const setField = (name: string, key: string, v: string) => {
    onChange({ ...value, [name]: { ...(value[name] ?? {}), [key]: v } });
  };
  const toggle = (name: string, next: boolean) => {
    if (name === "Öltanks" && oilTanksForced) return;
    setField(name, "selected", next ? "true" : "false");
  };

  const renderTankFields = (name: string) => {
    const entry = value[name] ?? {};
    const standort = entry.standort ?? "";
    return (
      <div className={styles.detail}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Anzahl</span>
          <input
            className={styles.input}
            value={entry.anzahl ?? ""}
            onChange={(e) => setField(name, "anzahl", ziffernFilter(e.target.value))}
            inputMode="numeric"
          />
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {name === "Öltanks" ? "Fassungsvermögen (Liter)" : "Füllgewicht (kg)"}
          </span>
          <input
            className={styles.input}
            value={entry.menge ?? ""}
            onChange={(e) => setField(name, "menge", ziffernFilter(e.target.value, { dezimal: true }))}
          />
        </div>
        <div className={mergeClasses(styles.field, styles.fullSpan)}>
          <span className={styles.fieldLabel}>Standort des Tanks</span>
          <div className={styles.radioRow}>
            {["im Gebäude", "außerhalb oberirdisch", "außerhalb unterirdisch"].map(
              (opt) => (
                <label key={opt} className={styles.radioOption}>
                  <input
                    type="radio"
                    checked={standort === opt}
                    onChange={() => setField(name, "standort", opt)}
                    style={{ accentColor: BRAND.purple }}
                  />
                  {opt}
                </label>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.list}>
      {UMWELT_ANLAGEN.map((name) => {
        const checked = isChecked(name);
        const isOil = name === "Öltanks";
        const isGas = name === "Gastanks";
        const isAbscheider = name === "Ölabscheider" || name === "Fettabscheider";
        const isSonstige = name === "Sonstige";
        const forced = isOil && oilTanksForced;
        return (
          <div
            key={name}
            className={mergeClasses(styles.card, forced && styles.cardForced)}
          >
            <label className={styles.head}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={checked}
                disabled={forced}
                onChange={(e) => toggle(name, e.target.checked)}
              />
              <span className={styles.labelText}>{name}</span>
            </label>
            {forced && (
              <div className={styles.hint}>
                Automatisch aktiviert aufgrund Auswahl „Ölheizung" im vorigen Schritt.
              </div>
            )}
            {checked && (isOil || isGas) && renderTankFields(name)}
            {checked && isAbscheider && (
              <div className={styles.detail}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Anzahl</span>
                  <input
                    className={styles.input}
                    value={value[name]?.anzahl ?? ""}
                    onChange={(e) => setField(name, "anzahl", ziffernFilter(e.target.value))}
                    inputMode="numeric"
                  />
                </div>
              </div>
            )}
            {checked && isSonstige && (
              <div className={styles.detail}>
                <div className={mergeClasses(styles.field, styles.fullSpan)}>
                  <span className={styles.fieldLabel}>Beschreibung</span>
                  <input
                    className={styles.input}
                    value={value[name]?.beschreibung ?? ""}
                    onChange={(e) => setField(name, "beschreibung", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UmweltChecklist;
